import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Server,
  Power,
  PowerOff,
  RotateCcw,
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Cpu,
  HardDrive,
  MemoryStick,
  Globe,
  Activity,
} from "lucide-react";

interface ContaboInstance {
  instanceId: number;
  displayName: string;
  name: string;
  status: string;
  imageId: string;
  productId: string;
  ipConfig?: {
    v4?: { ip: string };
    v6?: { ip: string };
  };
  cpuCores?: number;
  ramMb?: number;
  diskMb?: number;
  region?: string;
  createdDate?: string;
  additionalIps?: Array<{ v4?: { ip: string } }>;
}

const AdminContaboVPS = () => {
  const [instances, setInstances] = useState<ContaboInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  const callContabo = async (action: string, instanceId?: number) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not authenticated");

    const res = await supabase.functions.invoke("contabo-manage", {
      body: { action, instanceId },
    });

    if (res.error) throw new Error(res.error.message);
    return res.data;
  };

  const loadInstances = async () => {
    setLoading(true);
    try {
      const result = await callContabo("list-instances");
      if (result.success) {
        setInstances(result.data || []);
        toast.success("تم جلب السيرفرات بنجاح");
      } else {
        throw new Error(result.error);
      }
    } catch (e: any) {
      toast.error("فشل جلب السيرفرات: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: string, instanceId: number, label: string) => {
    const key = `${action}-${instanceId}`;
    setActionLoading(key);
    try {
      const result = await callContabo(action, instanceId);
      if (result.success) {
        toast.success(`تم ${label} السيرفر بنجاح`);
        setTimeout(() => loadInstances(), 3000);
      } else {
        throw new Error(result.error);
      }
    } catch (e: any) {
      toast.error(`فشل ${label}: ` + e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const testConnection = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const result = await callContabo("test-connection");
      setTestResult({
        success: result.success,
        message: result.success
          ? `✅ متصل بنجاح (${result.instanceCount} سيرفر)`
          : `❌ ${result.error}`,
      });
    } catch (e: any) {
      setTestResult({ success: false, message: `❌ ${e.message}` });
    } finally {
      setTestLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const s = status?.toLowerCase();
    if (s === "running") return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">🟢 يعمل</Badge>;
    if (s === "stopped") return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">🔴 متوقف</Badge>;
    if (s === "installing") return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">🟡 تثبيت</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  };

  const getIpAddress = (instance: ContaboInstance) => {
    return instance.ipConfig?.v4?.ip || "N/A";
  };

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Server className="w-5 h-5 text-primary" />
            إدارة سيرفر Contabo VPS
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={testConnection}
              disabled={testLoading}
            >
              {testLoading ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : <Activity className="w-4 h-4 ml-1" />}
              اختبار الاتصال
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadInstances}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : <RefreshCw className="w-4 h-4 ml-1" />}
              جلب السيرفرات
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Test Result */}
        {testResult && (
          <div className={`p-3 rounded-lg border ${testResult.success ? "bg-green-500/10 border-green-500/30" : "bg-red-500/10 border-red-500/30"}`}>
            <div className="flex items-center gap-2">
              {testResult.success ? <CheckCircle2 className="w-5 h-5 text-green-400" /> : <XCircle className="w-5 h-5 text-red-400" />}
              <span className="text-sm">{testResult.message}</span>
            </div>
          </div>
        )}

        {/* Instances */}
        {instances.length === 0 && !loading && (
          <div className="text-center py-8 text-muted-foreground">
            <Server className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>اضغط "جلب السيرفرات" لعرض سيرفراتك</p>
          </div>
        )}

        {loading && (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground mt-2">جاري جلب السيرفرات...</p>
          </div>
        )}

        {instances.map((instance) => (
          <div
            key={instance.instanceId}
            className="p-4 rounded-lg border border-border/50 bg-secondary/30 space-y-3"
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Server className="w-5 h-5 text-primary" />
                <div>
                  <h3 className="font-semibold text-foreground">
                    {instance.displayName || instance.name || `VPS #${instance.instanceId}`}
                  </h3>
                  <p className="text-xs text-muted-foreground">ID: {instance.instanceId}</p>
                </div>
              </div>
              {getStatusBadge(instance.status)}
            </div>

            {/* Specs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Globe className="w-4 h-4 shrink-0" />
                <span className="font-mono text-xs">{getIpAddress(instance)}</span>
              </div>
              {instance.cpuCores && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Cpu className="w-4 h-4 shrink-0" />
                  <span>{instance.cpuCores} vCPU</span>
                </div>
              )}
              {instance.ramMb && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MemoryStick className="w-4 h-4 shrink-0" />
                  <span>{Math.round(instance.ramMb / 1024)} GB RAM</span>
                </div>
              )}
              {instance.diskMb && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <HardDrive className="w-4 h-4 shrink-0" />
                  <span>{Math.round(instance.diskMb / 1024)} GB</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                className="text-green-400 border-green-500/30 hover:bg-green-500/10"
                onClick={() => handleAction("start", instance.instanceId, "تشغيل")}
                disabled={actionLoading === `start-${instance.instanceId}` || instance.status?.toLowerCase() === "running"}
              >
                {actionLoading === `start-${instance.instanceId}` ? (
                  <Loader2 className="w-4 h-4 animate-spin ml-1" />
                ) : (
                  <Power className="w-4 h-4 ml-1" />
                )}
                تشغيل
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                onClick={() => handleAction("stop", instance.instanceId, "إيقاف")}
                disabled={actionLoading === `stop-${instance.instanceId}` || instance.status?.toLowerCase() === "stopped"}
              >
                {actionLoading === `stop-${instance.instanceId}` ? (
                  <Loader2 className="w-4 h-4 animate-spin ml-1" />
                ) : (
                  <PowerOff className="w-4 h-4 ml-1" />
                )}
                إيقاف
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/10"
                onClick={() => handleAction("restart", instance.instanceId, "إعادة تشغيل")}
                disabled={actionLoading === `restart-${instance.instanceId}`}
              >
                {actionLoading === `restart-${instance.instanceId}` ? (
                  <Loader2 className="w-4 h-4 animate-spin ml-1" />
                ) : (
                  <RotateCcw className="w-4 h-4 ml-1" />
                )}
                إعادة تشغيل
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default AdminContaboVPS;
